//node_modules
import React, { useEffect, useContext, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// primereact components
import { Card } from "primereact/card";
import { Password } from "primereact/password";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

// components
import Footer from "../../components/Footer";

// redux
import { useLoginMutation } from "../../redux/services/auth";
import { useLazyGetMeQuery } from "../../redux/services/user";
import {
  useVerifyManagerOtpMutation,
  useResendManagerOtpMutation,
} from "../../redux/services/managerOtp";
import { setLoading } from "../../redux/slices/app.slice";

// utils
import getSubdomain from "../../utils/getSubdomain";

// context
import { ToastContext } from "../../App";

//styles
import * as styles from "./Login.module.css";

const STEP_LOGIN = "login";
const STEP_OTP = "otp";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showToast = useContext(ToastContext);

  const [step, setStep] = useState(STEP_LOGIN);
  const [startToken, setStartToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [remaining, setRemaining] = useState(120);
  const [mustResend, setMustResend] = useState(false);
  const [otpError, setOtpError] = useState(null);

  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [verifyManagerOtp, { isLoading: isVerifying }] =
    useVerifyManagerOtpMutation();
  const [resendManagerOtp, { isLoading: isResending }] =
    useResendManagerOtpMutation();
  const [
    getUser,
    {
      data: profile,
      isSuccess: isGetUserSuccess,
      isFetching: isGetUserFetching,
    },
  ] = useLazyGetMeQuery();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }, []);

  useEffect(() => {
    if (step !== STEP_OTP) return;
    setRemaining(120);
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(t);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [startToken, step]);

  useEffect(() => {
    if (isGetUserSuccess && !isGetUserFetching) {
      const role = profile.role;
      switch (role) {
        case "admin":
          navigate("/app/admin");
          break;
        case "chef":
          navigate("/app/chef");
          break;
      }
    }
  }, [isGetUserSuccess, isGetUserFetching]);

  const onSubmit = async (formData) => {
    dispatch(setLoading(true));
    try {
      const result = await login({
        email: formData.email,
        password: formData.password,
        subdomain: getSubdomain(),
      }).unwrap();
      dispatch(setLoading(false));

      if (result.start_token || result.startToken) {
        setStartToken(result.start_token || result.startToken);
        setStep(STEP_OTP);
      } else {
        showToast("success", "Login", "Login was successful");
        getUser();
      }
    } catch (e) {
      dispatch(setLoading(false));
      showToast("error", e?.status, e?.data?.message);
    }
  };

  const verifyOtp = async () => {
    setOtpError(null);
    try {
      const data = await verifyManagerOtp({
        start_token: startToken,
        otp,
      }).unwrap();
      const accessToken = data.access_token || data.accessToken;
      const refreshToken = data.refresh_token || data.refreshToken;
      if (accessToken) localStorage.setItem("access_token", accessToken);
      if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
      navigate("/app/manager");
    } catch (e) {
      const msg = e.data?.message || e.message || "Verification failed";
      if (msg === "invalid_otp") {
        setOtpError(
          "Invalid or expired code. Please request a new code (Resend).",
        );
        setMustResend(true);
      } else {
        setOtpError(msg);
      }
    }
  };

  const resendOtp = async () => {
    setOtpError(null);
    try {
      const data = await resendManagerOtp({
        start_token: startToken,
      }).unwrap();
      const token = data.start_token || data.startToken || startToken;
      setStartToken(token);
      setRemaining(120);
      setOtp("");
      setMustResend(false);
    } catch (e) {
      setOtpError(e.data?.message || e.message || "Resend failed");
    }
  };

  const otpLoading = isVerifying || isResending;

  return (
    <>
      <div className={styles.wrapper}>
        <Card title="Sign In" className={styles.card}>
          {step === STEP_LOGIN ? (
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
              <div className={styles.fieldContainer}>
                <label htmlFor="email" className="block mb-2">
                  Email
                </label>
                <InputText
                  id="email"
                  {...register("email", {
                    required: "Please enter your email",
                  })}
                  className={errors.email ? "p-invalid" : ""}
                  placeholder="Your Email"
                />
                {errors.email && (
                  <small className={`p-error ${styles.errorMessage}`}>
                    {errors.email.message}
                  </small>
                )}
              </div>

              <div className={styles.fieldContainer}>
                <label htmlFor="password" className="block mb-2">
                  Password
                </label>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Please enter your password" }}
                  render={({ field, fieldState }) => (
                    <>
                      <Password
                        id="password"
                        {...field}
                        toggleMask
                        feedback={false}
                        className={fieldState.invalid ? "p-invalid" : ""}
                        placeholder="Your Password"
                      />
                      {fieldState.error && (
                        <small className={`p-error ${styles.errorMessage}`}>
                          {fieldState.error.message}
                        </small>
                      )}
                    </>
                  )}
                />
              </div>

              <Button
                label="Sign In"
                type="submit"
                loading={isLoggingIn}
                className={`${styles.submitButton} p-button-primary`}
              />
            </form>
          ) : (
            <div className="p-fluid">
              <p className={styles.otpHint}>
                A verification code was sent to your email. Enter it below to
                complete login.
              </p>
              <div className={styles.fieldContainer}>
                <label className="block mb-2">Verification Code</label>
                <InputText
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="123456"
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    otp.length >= 4 &&
                    !mustResend &&
                    verifyOtp()
                  }
                />
              </div>
              <Button
                label="Verify"
                onClick={verifyOtp}
                loading={isVerifying}
                disabled={otpLoading || otp.length < 4 || mustResend}
                className={`${styles.submitButton} p-button-primary`}
              />
              <div className={styles.otpFooter}>
                <span className={styles.otpTimer}>
                  Code expires in: {remaining}s
                </span>
                <a
                  className={styles.otpResend}
                  onClick={
                    !otpLoading && remaining === 0 ? resendOtp : undefined
                  }
                  style={{
                    cursor:
                      !otpLoading && remaining === 0
                        ? "pointer"
                        : "not-allowed",
                    opacity: !otpLoading && remaining === 0 ? 1 : 0.5,
                  }}
                >
                  Resend
                </a>
              </div>
              {otpError && (
                <div style={{ color: "#c62828", marginTop: 8 }}>{otpError}</div>
              )}
            </div>
          )}
        </Card>
        <Footer />
      </div>
    </>
  );
};

export default Login;
